import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Lock,
  MapPin,
  Navigation,
  RefreshCw,
  Route,
  ShieldCheck,
  Users,
} from 'lucide-react';
import {
  fetchLiveCarerRecords,
  getStoredLiveCarerRecords,
  getStoredVisitVerificationRecords,
  LIVE_CARER_TRACKING_KEY,
  VISIT_VERIFICATION_KEY,
  type CarerStatus,
  type LiveCarerTrackingRecord,
  type VisitVerificationRecord,
} from '@/utils/liveTelemetryApi';

const defaultTracking: LiveCarerTrackingRecord[] = [
  {
    caregiverId: 'carer-adrian',
    caregiverName: 'Adrian Popescu',
    email: 'AdrianPopescu@carer.com',
    status: 'available',
    currentClient: 'Waiting for check-in',
    currentVisit: 'Scheduled route',
    currentTask: 'No active task',
    address: 'Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN',
    latitude: 52.5868,
    longitude: 1.7235,
    locationSource: 'scheduled-address',
    scheduleMatched: true,
    payrollLocked: false,
    lastUpdated: new Date().toISOString(),
  },
  {
    caregiverId: 'carer-daniel',
    caregiverName: 'Daniel Moore',
    email: 'daniel.moore@fuchsiahomecare.co.uk',
    status: 'offline',
    currentClient: 'Not checked in',
    currentVisit: 'No active visit',
    currentTask: 'Awaiting scheduled visit',
    address: 'Great Yarmouth, NR31 0LN',
    latitude: 52.5879,
    longitude: 1.7261,
    locationSource: 'scheduled-address',
    scheduleMatched: false,
    payrollLocked: false,
    lastUpdated: new Date(Date.now() - 1000 * 60 * 18).toISOString(),
  },
  {
    caregiverId: 'carer-amelia',
    caregiverName: 'Amelia Patel',
    email: 'amelia.patel@fuchsiahomecare.co.uk',
    status: 'completed',
    currentClient: 'Eileen Cooper',
    currentVisit: 'Dementia Support',
    currentTask: 'Visit completed and payroll locked',
    address: 'River View, Great Yarmouth, NR31 0LN',
    latitude: 52.6041,
    longitude: 1.7314,
    locationSource: 'scheduled-address',
    scheduleMatched: true,
    payrollLocked: true,
    lastUpdated: new Date(Date.now() - 1000 * 60 * 4).toISOString(),
  },
];

const defaultVerificationRecords: VisitVerificationRecord[] = [
  {
    visitId: 'visit-margaret-morning',
    caregiverId: 'carer-adrian',
    caregiverName: 'Adrian Popescu',
    clientName: 'Margaret Williams',
    scheduledStartAt: new Date(new Date().setHours(9, 0, 0, 0)).toISOString(),
    scheduledEndAt: new Date(new Date().setHours(10, 0, 0, 0)).toISOString(),
    scheduledDurationMinutes: 60,
    actualCheckInAt: new Date(Date.now() - 1000 * 60 * 22).toISOString(),
    realDurationMinutes: 22,
    varianceMinutes: -38,
    distanceFromClientMeters: 18,
    gpsAccuracyMeters: 12,
    geofenceStatus: 'inside',
    scheduleStatus: 'on-time',
    payrollLocked: false,
    hoursBlocked: false,
    exceptions: [],
    taskEvidence: [
      {
        id: 'evidence-1',
        task: 'Morning personal care',
        status: 'completed',
        note: 'Completed and client comfortable.',
        recordedAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
        caregiverId: 'carer-adrian',
        caregiverName: 'Adrian Popescu',
      },
    ],
    auditTrail: [
      {
        id: 'audit-1',
        type: 'check-in',
        label: 'Carer checked in from scheduled visit',
        createdAt: new Date(Date.now() - 1000 * 60 * 22).toISOString(),
        locationSource: 'device-gps',
        latitude: 52.5868,
        longitude: 1.7235,
        distanceFromClientMeters: 18,
      },
      {
        id: 'audit-2',
        type: 'task-evidence',
        label: 'Completed task: Morning personal care',
        createdAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
      },
    ],
    updatedAt: new Date().toISOString(),
  },
  {
    visitId: 'visit-george-companionship',
    caregiverId: 'carer-daniel',
    caregiverName: 'Daniel Moore',
    clientName: 'George Taylor',
    scheduledStartAt: new Date(new Date().setHours(10, 30, 0, 0)).toISOString(),
    scheduledEndAt: new Date(new Date().setHours(12, 0, 0, 0)).toISOString(),
    scheduledDurationMinutes: 90,
    distanceFromClientMeters: 410,
    gpsAccuracyMeters: 28,
    geofenceStatus: 'outside',
    scheduleStatus: 'unscheduled',
    payrollLocked: false,
    hoursBlocked: true,
    exceptions: ['GPS outside client address by 410m', 'Hours blocked until administrator review'],
    taskEvidence: [],
    auditTrail: [
      {
        id: 'audit-3',
        type: 'gps-check',
        label: 'Check-in blocked because GPS is outside the client geofence',
        createdAt: new Date(Date.now() - 1000 * 60 * 8).toISOString(),
        locationSource: 'device-gps',
        latitude: 52.609,
        longitude: 1.699,
        distanceFromClientMeters: 410,
      },
    ],
    updatedAt: new Date(Date.now() - 1000 * 60 * 8).toISOString(),
  },
];

function formatLastSeen(value: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000));

  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
}

function formatTime(value?: string): string {
  if (!value) return 'Not recorded';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function isStale(value: string): boolean {
  return Date.now() - new Date(value).getTime() > 1000 * 60 * 10;
}

function statusStyles(status: CarerStatus) {
  if (status === 'in-progress') return 'bg-blue-100 text-blue-800 border-blue-200';
  if (status === 'completed') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (status === 'available') return 'bg-slate-100 text-slate-700 border-slate-200';
  return 'bg-amber-100 text-amber-800 border-amber-200';
}

export const LiveCarerTracking: React.FC = () => {
  const [records, setRecords] = useState<LiveCarerTrackingRecord[]>(() =>
    getStoredLiveCarerRecords(defaultTracking)
  );
  const [verificationRecords, setVerificationRecords] = useState<VisitVerificationRecord[]>(() =>
    getStoredVisitVerificationRecords(defaultVerificationRecords)
  );
  const [selectedRecord, setSelectedRecord] = useState<LiveCarerTrackingRecord | null>(null);
  const [selectedVisit, setSelectedVisit] = useState<VisitVerificationRecord | null>(null);
  const [isServerConnected, setIsServerConnected] = useState(false);

  const refreshRecords = useCallback(async () => {
    const result = await fetchLiveCarerRecords(defaultTracking);
    setRecords(result.data.length > 0 ? result.data : defaultTracking);
    setVerificationRecords(getStoredVisitVerificationRecords(defaultVerificationRecords));
    setIsServerConnected(result.ok);
  }, []);

  useEffect(() => {
    void refreshRecords();
    const timer = window.setInterval(() => void refreshRecords(), 10_000);
    const handleStorage = (event: StorageEvent) => {
      if (event.key === LIVE_CARER_TRACKING_KEY) void refreshRecords();
      if (event.key === VISIT_VERIFICATION_KEY) setVerificationRecords(getStoredVisitVerificationRecords(defaultVerificationRecords));
    };
    const handleFocus = () => void refreshRecords();

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshRecords]);

  const summary = useMemo(() => {
    return {
      active: records.filter((record) => record.status === 'in-progress').length,
      payrollLocked: records.filter((record) => record.payrollLocked).length,
      stale: records.filter((record) => isStale(record.lastUpdated)).length,
      scheduleMatched: records.filter((record) => record.scheduleMatched).length,
      blockedHours: verificationRecords.filter((record) => record.hoursBlocked).length,
      geofenceAlerts: verificationRecords.filter((record) => record.geofenceStatus === 'outside').length,
      shortVisits: verificationRecords.filter((record) => (record.realDurationMinutes ?? 999) < 15 && record.actualCheckOutAt).length,
      lateStarts: verificationRecords.filter((record) => record.scheduleStatus === 'late').length,
    };
  }, [records, verificationRecords]);

  const openMap = (record: LiveCarerTrackingRecord) => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${record.latitude},${record.longitude}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-5">
            <div className="rounded-lg bg-[#6c3a80]/10 p-4 text-[#6c3a80]">
              <MapPin className="h-8 w-8" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                Administrator Live Control
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Live Carer Tracking</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                See each carer's last known location, active visit, current task, schedule match and payroll lock status.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void refreshRecords()}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#5e3474]"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh Live Data
          </button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Active Visits</p>
            <Activity className="h-5 w-5 text-blue-700" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{summary.active}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Schedule Matched</p>
            <ShieldCheck className="h-5 w-5 text-emerald-700" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{summary.scheduleMatched}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Payroll Locked</p>
            <CheckCircle className="h-5 w-5 text-[#6c3a80]" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{summary.payrollLocked}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Stale Signals</p>
            <Clock className="h-5 w-5 text-amber-700" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{summary.stale}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">GPS Alerts</p>
            <AlertTriangle className="h-5 w-5 text-red-700" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{summary.geofenceAlerts}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Blocked Hours</p>
            <Lock className="h-5 w-5 text-red-700" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{summary.blockedHours}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Short Visits</p>
            <Clock className="h-5 w-5 text-orange-700" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{summary.shortVisits}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Late Starts</p>
            <Route className="h-5 w-5 text-amber-700" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{summary.lateStarts}</p>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-950">Carer Live Board</h2>
              <p className="mt-1 text-sm text-slate-600">
                Auto-refreshes every 10 seconds using {isServerConnected ? 'the API server' : 'local fallback'}.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  isServerConnected ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                }`}
              >
                {isServerConnected ? 'API connected' : 'Local fallback'}
              </span>
              <Users className="h-5 w-5 text-slate-400" />
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {records.map((record) => (
              <button
                key={record.caregiverId}
                type="button"
                onClick={() => setSelectedRecord(record)}
                className="w-full rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-[#6c3a80]/40 hover:bg-[#6c3a80]/5"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-bold text-slate-950">{record.caregiverName}</h3>
                      <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${statusStyles(record.status)}`}>
                        {record.status.replace(/-/g, ' ')}
                      </span>
                      {isStale(record.lastUpdated) && (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                          signal stale
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-500">{record.email}</p>
                    <p className="mt-3 text-sm text-slate-700">
                      <strong>Client:</strong> {record.currentClient ?? 'No active client'}
                    </p>
                    <p className="mt-1 text-sm text-slate-700">
                      <strong>Task:</strong> {record.currentTask ?? 'No active task'}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
                      <span className={`rounded-full px-2 py-1 ${
                        record.geofenceStatus === 'outside'
                          ? 'bg-red-100 text-red-800'
                          : record.geofenceStatus === 'inside'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-slate-100 text-slate-700'
                      }`}>
                        GPS {record.geofenceStatus ?? 'waiting'}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                        {record.scheduleStatus?.replace(/-/g, ' ') ?? 'schedule pending'}
                      </span>
                      {typeof record.distanceFromClientMeters === 'number' && (
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                          {Math.round(record.distanceFromClientMeters)}m from client
                        </span>
                      )}
                      {(record.exceptions?.length ?? 0) > 0 && (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-800">
                          {record.exceptions!.length} exception{record.exceptions!.length === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-slate-600 lg:text-right">
                    <p>{formatLastSeen(record.lastUpdated)}</p>
                    <p className="mt-1 capitalize">{record.locationSource.replace(/-/g, ' ')}</p>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openMap(record);
                      }}
                      className="mt-3 inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <Navigation className="h-4 w-4" />
                      Open Map
                    </button>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-slate-950">Selected Carer Control</h2>
          {selectedRecord ? (
            <div className="mt-5 space-y-4">
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-500">Carer</p>
                <p className="mt-1 text-lg font-bold text-slate-950">{selectedRecord.caregiverName}</p>
                <p className="text-sm text-slate-600">{selectedRecord.email}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-500">Location</p>
                <p className="mt-1 text-sm text-slate-700">{selectedRecord.address}</p>
                <p className="mt-2 text-xs text-slate-500">
                  {selectedRecord.latitude.toFixed(5)}, {selectedRecord.longitude.toFixed(5)}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-500">Schedule Match</p>
                  <p className={`mt-1 font-bold ${selectedRecord.scheduleMatched ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {selectedRecord.scheduleMatched ? 'Verified' : 'Needs review'}
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-500">Payroll Lock</p>
                  <p className={`mt-1 font-bold ${selectedRecord.payrollLocked ? 'text-emerald-700' : 'text-slate-700'}`}>
                    {selectedRecord.payrollLocked ? 'Locked' : 'Open'}
                  </p>
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-500">Current Visit Control</p>
                <p className="mt-2 text-sm text-slate-700">
                  Check-in: {formatTime(selectedRecord.actualCheckInAt)}
                </p>
                <p className="text-sm text-slate-700">
                  Check-out: {formatTime(selectedRecord.actualCheckOutAt)}
                </p>
                <p className="text-sm text-slate-700">
                  Planned vs real: {selectedRecord.scheduledDurationMinutes ?? 0} min scheduled, {selectedRecord.realDurationMinutes ?? 0} min real
                </p>
                {(selectedRecord.exceptions?.length ?? 0) > 0 && (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-900">
                    {selectedRecord.exceptions!.join('; ')}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => openMap(selectedRecord)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#5e3474]"
              >
                <Navigation className="h-4 w-4" />
                Open Current Location
              </button>
            </div>
          ) : (
            <p className="mt-5 rounded-lg bg-slate-50 p-4 text-sm leading-6 text-slate-600">
              Select a carer from the live board to inspect location, task, schedule verification and payroll lock status.
            </p>
          )}
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-950">Live Map Status</h2>
              <p className="mt-1 text-sm text-slate-600">
                Visual control of active carers, stale signals and GPS exceptions.
              </p>
            </div>
            <MapPin className="h-6 w-6 text-[#6c3a80]" />
          </div>
          <div className="relative mt-5 h-[360px] overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(148,163,184,.25)_1px,transparent_1px),linear-gradient(0deg,rgba(148,163,184,.25)_1px,transparent_1px)] bg-[length:42px_42px]" />
            <div className="absolute left-6 top-6 rounded-md bg-white/90 px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm">
              Great Yarmouth operations area
            </div>
            {records.map((record, index) => {
              const left = 18 + ((record.longitude + 1.75) * 480 + index * 11) % 68;
              const top = 18 + ((record.latitude - 52.55) * 520 + index * 13) % 66;
              const alert = record.geofenceStatus === 'outside' || isStale(record.lastUpdated);
              return (
                <button
                  key={`map-${record.caregiverId}`}
                  type="button"
                  onClick={() => setSelectedRecord(record)}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-4 p-2 shadow-lg transition hover:scale-105 ${
                    alert
                      ? 'border-red-200 bg-red-600 text-white'
                      : record.status === 'in-progress'
                        ? 'border-blue-200 bg-blue-600 text-white'
                        : 'border-emerald-200 bg-emerald-600 text-white'
                  }`}
                  style={{ left: `${left}%`, top: `${top}%` }}
                  title={`${record.caregiverName} - ${record.status}`}
                >
                  <MapPin className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-[#6c3a80]" />
            <h2 className="text-xl font-bold text-slate-950">Visit Loss-Control Rules</h2>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {[
              ['GPS check-in/out', 'Device GPS is captured at visit start and compared with the client address.'],
              ['Geofence block', 'Hours are blocked when GPS is outside the client address tolerance.'],
              ['Scheduled visit lock', 'Payroll can only be created from a scheduled visit card.'],
              ['Planned vs real time', 'Real check-in/out minutes are compared with the scheduled duration.'],
              ['Short visit detection', 'Visits under 15 minutes are flagged for administrator review.'],
              ['Late-start alert', 'Check-ins more than 10 minutes late create an audit exception.'],
              ['Task evidence', 'Completed and not-completed tasks are stored as digital proof.'],
              ['Full visit audit', 'GPS checks, check-in, task evidence, exceptions and payroll lock are kept per visit.'],
            ].map(([title, description]) => (
              <div key={title} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="font-semibold text-slate-950">{title}</p>
                <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-[#6c3a80]" />
            <div>
              <h2 className="text-xl font-bold text-slate-950">Visit Verification Database</h2>
              <p className="mt-1 text-sm text-slate-600">
                Administrator view of scheduled hours, real hours, GPS verification, blocked payroll and audit evidence.
              </p>
            </div>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
            {verificationRecords.length} visit record{verificationRecords.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-3">Visit</th>
                <th className="px-3 py-3">Carer</th>
                <th className="px-3 py-3">GPS</th>
                <th className="px-3 py-3">Time</th>
                <th className="px-3 py-3">Payroll</th>
                <th className="px-3 py-3">Evidence</th>
                <th className="px-3 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {verificationRecords.map((record) => (
                <tr key={record.visitId}>
                  <td className="px-3 py-3">
                    <p className="font-semibold text-slate-950">{record.clientName}</p>
                    <p className="text-xs text-slate-500">{formatTime(record.scheduledStartAt)}</p>
                  </td>
                  <td className="px-3 py-3 text-slate-700">{record.caregiverName}</td>
                  <td className="px-3 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      record.geofenceStatus === 'outside'
                        ? 'bg-red-100 text-red-800'
                        : record.geofenceStatus === 'inside'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                    }`}>
                      {record.geofenceStatus}
                    </span>
                    <p className="mt-1 text-xs text-slate-500">
                      {typeof record.distanceFromClientMeters === 'number' ? `${Math.round(record.distanceFromClientMeters)}m` : 'waiting'}
                    </p>
                  </td>
                  <td className="px-3 py-3 text-slate-700">
                    <p>{record.scheduledDurationMinutes} min scheduled</p>
                    <p>{record.realDurationMinutes ?? 0} min real</p>
                    <p className={record.varianceMinutes && record.varianceMinutes < -15 ? 'font-semibold text-amber-700' : 'text-slate-500'}>
                      variance {record.varianceMinutes ?? 0} min
                    </p>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      record.hoursBlocked
                        ? 'bg-red-100 text-red-800'
                        : record.payrollLocked
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-slate-100 text-slate-700'
                    }`}>
                      {record.hoursBlocked ? 'blocked' : record.payrollLocked ? 'locked' : 'open'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-slate-700">
                    {record.taskEvidence.length} task proof
                    <p className="text-xs text-slate-500">{record.auditTrail.length} audit events</p>
                  </td>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      onClick={() => setSelectedVisit(record)}
                      className="rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      View audit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {selectedVisit && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-950">Visit Audit - {selectedVisit.clientName}</h2>
              <p className="mt-1 text-sm text-slate-600">
                {selectedVisit.caregiverName} | {formatTime(selectedVisit.scheduledStartAt)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedVisit(null)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Close audit
            </button>
          </div>
          {selectedVisit.exceptions.length > 0 && (
            <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-900">
              {selectedVisit.exceptions.join('; ')}
            </div>
          )}
          <div className="mt-5 grid gap-6 lg:grid-cols-2">
            <div>
              <h3 className="font-semibold text-slate-950">Audit trail</h3>
              <div className="mt-3 space-y-3">
                {selectedVisit.auditTrail.map((event) => (
                  <div key={event.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-semibold capitalize text-slate-900">{event.type.replace(/-/g, ' ')}</p>
                      <p className="text-xs text-slate-500">{formatTime(event.createdAt)}</p>
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{event.label}</p>
                    {typeof event.distanceFromClientMeters === 'number' && (
                      <p className="mt-1 text-xs text-slate-500">
                        {Math.round(event.distanceFromClientMeters)}m from client | {event.locationSource}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-slate-950">Digital task proof</h3>
              <div className="mt-3 space-y-3">
                {selectedVisit.taskEvidence.map((evidence) => (
                  <div key={evidence.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-semibold text-slate-900">{evidence.task}</p>
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                        evidence.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {evidence.status.replace(/-/g, ' ')}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{evidence.note || 'No extra note.'}</p>
                    <p className="mt-1 text-xs text-slate-500">{formatTime(evidence.recordedAt)}</p>
                  </div>
                ))}
                {selectedVisit.taskEvidence.length === 0 && (
                  <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                    No task evidence recorded yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-900">
        This page is wired to the carer portal telemetry through the API server. If the API is unavailable,
        it keeps working locally and reconnects automatically on the next polling refresh.
      </section>
    </div>
  );
};
